import libphonenumber from 'google-libphonenumber';

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

/**
 * Parse a phone number in any format into its country code and national number.
 *
 * Handles all storage formats we've seen in the wild:
 *   "+1 8133446984"   → { countryCode: "+1", nationalNumber: "8133446984" }
 *   "+18133446984"    → { countryCode: "+1", nationalNumber: "8133446984" }
 *   "+442079460958"   → { countryCode: "+44", nationalNumber: "2079460958" }
 *   "8133446984"      → { countryCode: "+1", nationalNumber: "8133446984" }
 *   "(813) 344-6984"  → { countryCode: "+1", nationalNumber: "8133446984" }
 *   null / undefined  → { countryCode: "+1", nationalNumber: "" }
 */
export const parsePhoneValue = (
  value: string | null | undefined,
  defaultCountryCode = '+1'
): { countryCode: string; nationalNumber: string } => {
  const empty = { countryCode: defaultCountryCode, nationalNumber: '' };

  if (!value) return empty;

  // Fast path: if it starts with + and has a space, split on first space.
  // This handles the standard DB format ("+1 8133446984") and partial numbers
  // during typing ("+1 3") without libphonenumber choking on incomplete numbers.
  if (value.startsWith('+')) {
    const spaceIdx = value.indexOf(' ');
    if (spaceIdx !== -1) {
      const cc = value.substring(0, spaceIdx);
      const national = value.substring(spaceIdx + 1).replace(/\D/g, '');
      return { countryCode: cc, nationalNumber: national };
    }
  }

  // No space: use libphonenumber for complete numbers like "+18133446984"
  try {
    const ccDigits = parseInt(defaultCountryCode.replace(/\D/g, ''), 10);
    const defaultRegion = ccDigits ? phoneUtil.getRegionCodeForCountryCode(ccDigits) : 'US';
    const region = value.startsWith('+') ? 'ZZ' : defaultRegion;
    const parsed = phoneUtil.parse(value, region);
    const cc = parsed.getCountryCode();
    const national = parsed.getNationalNumber();

    if (!cc || !national) return empty;

    return {
      countryCode: `+${cc}`,
      nationalNumber: `${national}`,
    };
  } catch {
    // Unparseable — strip non-digits and return with the default code.
    const digits = value.replace(/\D/g, '');
    return digits ? { countryCode: defaultCountryCode, nationalNumber: digits } : empty;
  }
};
